import { Box } from '@mui/material';
import Chart from 'react-apexcharts';
import { WidgetWrapper } from '../WidgetWrapper';
import { cardChartLabel } from '../style';
import { getRuntimeChartOptions, CHART_LABEL } from '../../config';
import { ChartData } from '../../types';
import { AssetType } from 'src/types/assets';
import { PUMP_RUNTIME_TAG_NAME } from 'src/features/sites';
import { checkIsEmptyObject } from 'src/utilities';
import { NoDataAvailableBox } from '../NoDataAvailableBox';
import { useTrendsWeeklyData } from 'src/features/sites/hooks/useTrendsWeeklyData';
import { useGetLatestGatewayReadings } from 'src/hooks/useGetLatestGatewayReadings';

type Props = {
  title: string;
  values: ChartData[] | null;
  isEditMode?: boolean;
  widgetId?: string;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  asset: AssetType | undefined;
  siteTimeZone: string;
};

export const RunTimeChart = ({
  title,
  values,
  isEditMode,
  widgetId,
  toggleAddOrRemoveHandler,
  testId,
  asset,
  siteTimeZone,
}: Props) => {
  const runTimeAsset = asset?.assignedTags?.find(
    (tag) => tag?.tagName?.toLowerCase() === PUMP_RUNTIME_TAG_NAME
  );

  const assignedTagId = runTimeAsset?.assignedTagId;
  const gatewayId = runTimeAsset?.assignedGatewayId;

  const { readings } = useTrendsWeeklyData({
    gatewayId: gatewayId ?? '',
    tagId: assignedTagId,
    siteTimeZone: siteTimeZone,
  });

  const { readings: latestReadings } = useGetLatestGatewayReadings({
    gatewayId: gatewayId,
    tagId: assignedTagId,
  });
  const latestReadingsData = assignedTagId && latestReadings[assignedTagId];
  // NOTE: `latestReadingsValues` is will either be `{}` or an obect of the form `{"YYYY-MM-DD": value}`
  // It is necessary to truncate the timestamp key because the latest endpoint returns a full isoformatted timestamp,
  // unlike the `latestReadings` which already has keys in YYYY-MM-DD format.
  // so that is matches the key-value pairs `readingsRawInputNameMax` object below since they are combined into a single
  // object `sumReadingsValues`
  // This same workaround is also used in src/features/widgets/components/CycleCountChart/CycleCountChart.tsx
  const latestReadingsValues = Object.entries(latestReadingsData?.values ?? {}).reduce(
    (_acc, [timestamp, value]) => {
      const truncatedTimestamp: any = timestamp.slice(0, 10);
      return { [truncatedTimestamp]: value };
    },
    {}
  );

  const readingsRawInputName =
    readings && !checkIsEmptyObject(readings) && assignedTagId && readings[assignedTagId];

  const readingsRawInputNameMax = readingsRawInputName?.max;

  const sumReadingsValues = {
    ...readingsRawInputNameMax,
    ...latestReadingsValues,
  };

  const readingValuesArray = sumReadingsValues && Object.entries(sumReadingsValues);

  const initialValues: ChartData[] = readingValuesArray?.flatMap((item: any) => {
    const time = item[0];
    const month = time.slice(5, 7);
    const day = time.slice(8, 10);
    const value = item[1] / 3600;
    const formattedDate = `${month}.${day}`;
    return {
      label: formattedDate,
      value: Number(value.toFixed(2)),
    };
  });

  const isLiveData = !!values?.length;
  const liveDataItem = isLiveData ? values![0] : null;

  const dataValuesFinal: ChartData[] = initialValues.map((item) => {
    const isTheLastDay = item.label === liveDataItem?.label;
    return {
      label: isTheLastDay ? liveDataItem.label : item.label,
      value: isTheLastDay ? liveDataItem.value : item.value,
    };
  });

  const noDataAvailable = !dataValuesFinal.length && !initialValues.length;

  const fullTitle = `${title} Runtime`;
  const options = dataValuesFinal
    ? getRuntimeChartOptions(
        dataValuesFinal?.map((x) => x.label),
        dataValuesFinal?.map((x) => x.value)
      )
    : getRuntimeChartOptions([]);
  const series = dataValuesFinal
    ? [{ name: 'Runtime', data: dataValuesFinal?.map((x) => x.value) }]
    : [];

  const days = options.xaxis?.categories;

  return (
    <WidgetWrapper
      title={fullTitle}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={dataValuesFinal !== null}
      isTrendWidget={true}
      options={options}
      series={series}
      typeChart="bar"
      noDataAvailable={noDataAvailable}
    >
      {noDataAvailable ? (
        <NoDataAvailableBox />
      ) : (
        <Chart
          data-sm={days.map((day: string) => day)}
          options={options}
          series={series}
          type="bar"
          width="100%"
          height="90%"
        />
      )}
      <Box display="span" sx={cardChartLabel}>
        {CHART_LABEL}
      </Box>
    </WidgetWrapper>
  );
};
